<template>
  <!-- PLACE ON DOCUMENT SWITCH -->
  <CustomSwitchWrapper
    :key="switchRemountKey"
    class="mb-2"
    data-test-id="place-signatures"
    :disabled="
      !createStore.committedHasInvitees ||
      createStore.isLoading ||
      !globalStore.pdfPreviewRendered
    "
    :initial-value="switchDisplayValue"
    :label="i18n.t('labels.place_signatures_on_document')"
    label-class="drawer-subhead"
    :switch-id="i18n.t('labels.place_signatures_on_document')"
    @value-changed="updateSignaturesPreview"
  >
    <template #label-append>
      <v-menu location="left top" max-width="300" open-on-click open-on-hover>
        <template #activator="{ props: ActivatorProps }">
          <v-icon
            v-bind="ActivatorProps"
            aria-label="Place singature hint"
            class="icon"
            color="grey"
            size="13"
          >
            far fa-circle-info
          </v-icon>
        </template>
        <v-card class="pa-3" flat>
          <v-card-text class="pa-0">
            {{ i18n.t('texts.place_signatures_hint') }}
          </v-card-text>
        </v-card>
      </v-menu>
    </template>
  </CustomSwitchWrapper>
  <ConfirmationDialog ref="confirmationDialog" />
  <!-- PLACE ON DOCUMENT SWITCH -->
</template>
<script setup lang="ts">
import CustomSwitchWrapper from '@/components/inputs/CustomSwitchWrapper.vue';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import { useCreateStore } from '@/stores/create.ts';
import { useI18n } from 'vue-i18n';
import { useGlobalStore } from '@/stores/global.ts';
import {
  injectStrict,
  PlaceHolderComposableKey
} from '@/composables/placement/placementSymbols.ts';
import {
  persistSigneePlaceholders,
  scrollToPlaceholder
} from '@/composables/placement/usePlacementUtility.ts';
import type { Placeholder } from '@/composables/placement/placementTypes.ts';
import type { Signee } from '@/types/Signee.ts';
import { useDisplay } from 'vuetify';
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import type { SigneeGroup } from '@/types/deepsign/SigneeGroup.ts';
import {
  findGroupSigningForSlot,
  getDefaultSigneeFileId,
  hasSigneePlacementOnFile,
  isSigneeGroupQuorumSet,
  isSigneePlacementSatisfiedOnFile,
  persistQuorumSlotPlaceholder,
  resolveSigneeSignings
} from '@/utils/signeeSignings.ts';

const createStore = useCreateStore();
const globalStore = useGlobalStore();
const display = useDisplay();
const i18n = useI18n();
const route = useRoute();
const confirmationDialog = ref<InstanceType<typeof ConfirmationDialog>>();
// Remounts CustomSwitchWrapper so cancel after an OFF click restores ON
// (the wrapper only syncs when initialValue changes).
const switchRemountKey = ref(0);
// Keeps the switch visually ON while missing invitees are being placed, since
// completeness stays false until persist finishes.
const optimisticOn = ref(false);

// The creator owns every placeholder in the draft/create flow, so they may
// always modify/remove positions there regardless of the (possibly still false)
// per-signee autograph policy a freshly added signee starts with. Mirrors the
// draft handling in `getPlaceholderDraggableInitialState`.
const isDraftFlow = computed(
  () => route.name === 'document-create' || route.name === 'p-document-create'
);
const {
  placePlaceholdersAutomatically,
  resetPlaceholders,
  placeholderTemplateRefs
} = injectStrict(PlaceHolderComposableKey);

// The file placements are currently scoped to. In envelope mode this is the
// selected file; otherwise the document's single file.
const selectedFileId = computed(
  () =>
    globalStore.selectedDocumentFileId ??
    getDefaultSigneeFileId(createStore.document)
);

// Whether the signee ACTUALLY has a placement of their own on the file. Used
// where a real, removable position is required (see `removeAllSignatures`).
// Quorum members never own a placement here — the position lives on the
// group, not the member — so this always reads false for them; that's fine
// for removal (their group's slot isn't touched by this per-signee switch).
function hasPlacementOnFile(signee: Signee, fileId: string | null): boolean {
  return hasSigneePlacementOnFile(signee, fileId, createStore.document);
}

// Whether the signee's OBLIGATION to place a signature on the file is
// satisfied — used for the "is everyone placed" completeness checks below.
// Unlike `hasPlacementOnFile`, this is quorum-aware: a quorum member is
// satisfied either because their group's shared slot is placed, or because
// they don't own a slot at all (only the first `requiredSignatures` members
// do — see `isSigneePlacementSatisfiedOnFile`).
function isPlacementSatisfiedOnFile(
  signee: Signee,
  fileId: string | null
): boolean {
  return isSigneePlacementSatisfiedOnFile(signee, fileId, createStore.document);
}

// This switch lives in the drawer, BEHIND the signing-workflow dialog, so it
// reads `committedSignees` — the participants as actually saved on the document
// — and never `filteredSignees`, which resolves to the dialog's draft while it
// is open. Reading the draft made the switch flip OFF the moment an invitee was
// added in the dialog (a not-yet-created signee has no placement, so
// completeness read false) even though nothing had been confirmed, and it
// dragged `placeSignaturesModel` down with it.

// Signees that already have at least one signature placed on the selected file.
const signeesPlacedOnSelectedFile = computed(() =>
  createStore.committedSignees.filter((signee) =>
    hasPlacementOnFile(signee, selectedFileId.value)
  )
);

// ON only when every SIGN invitee's placement obligation is satisfied on the
// selected file — quorum group members are covered once their group's shared
// slot is placed (or automatically, if they don't own a slot at all).
const allSigneesPlacedOnSelectedFile = computed(
  () =>
    createStore.committedSignees.length > 0 &&
    createStore.committedSignees.every((signee) =>
      isPlacementSatisfiedOnFile(signee, selectedFileId.value)
    )
);

const switchDisplayValue = computed(
  () =>
    optimisticOn.value ||
    createStore.isAutoPlacingNewSignees ||
    allSigneesPlacedOnSelectedFile.value
);

// Whether the signee's placement on the selected file may be modified/removed.
// The autograph-modify permission can be granted per file (signing-level policy)
// even when the signee-level policy forbids it, so honour the per-file policy
// first and fall back to the signee-level one. `filteredSignees` may lack
// `signings`, so resolve them from the document's signee groups.
function canModifyOnSelectedFile(signee: Signee): boolean {
  if (isDraftFlow.value) return true;
  const signings = resolveSigneeSignings(signee, createStore.document);
  const signing = signings.find((item) => item.fileId === selectedFileId.value);
  return Boolean(
    signing?.policy?.canModifyAutographPosition ??
    signee.policy?.canModifyAutographPosition
  );
}

// Steady state: placeSignaturesModel is true only when all signatures for the
// selected file are placed. Dependent UI (move-signatures menu, auto-place on
// invitee add) follows that completeness flag.
watch(
  allSigneesPlacedOnSelectedFile,
  (placed) => {
    // Confirm just committed new signees and is placing their placeholders
    // right now: completeness is false only until that finishes, so hold the
    // current state rather than flashing the whole dependent UI off and on.
    if (!placed && createStore.isAutoPlacingNewSignees) return;
    createStore.placeSignaturesModel = placed;
    if (placed) {
      optimisticOn.value = false;
    }
  },
  { immediate: true }
);

function placeSignaturesAutomatically() {
  createStore.isLoading = true;
  optimisticOn.value = true;
  //timeout to ensure the switch gets disabled during switch animation
  setTimeout(async () => {
    try {
      if (
        createStore.committedSignees &&
        createStore.committedSignees.length > 0
      ) {
        const missingSignees = createStore.committedSignees.filter(
          (signee) => !isPlacementSatisfiedOnFile(signee, selectedFileId.value)
        );
        if (missingSignees.length === 0) {
          return;
        }

        const placeholdersToUpdate =
          await placePlaceholdersAutomatically(missingSignees);
        await persistSigneePlaceholders(
          createStore,
          placeholdersToUpdate,
          placeholderTemplateRefs?.value,
          createStore.committedSignees,
          createStore.document
        );
        if (display.mdAndDown.value) {
          globalStore.drawer = false;
        }

        if (placeholdersToUpdate && placeholdersToUpdate.length > 0) {
          scrollToPlaceholder(placeholdersToUpdate[0] as Placeholder, true);
        }
      }
    } finally {
      createStore.isLoading = false;
      // Wait for store/computed to settle after persist before dropping
      // optimism, so a successful place does not briefly flash OFF.
      await nextTick();
      optimisticOn.value = false;
      if (!allSigneesPlacedOnSelectedFile.value) {
        switchRemountKey.value += 1;
      }
    }
  }, 1000);
}

// Clears every quorum group's shared slot(s) on the selected file.
// `removeSignature` (the per-signee endpoint) never touches these — quorum
// members never own a placement of their own, the position lives on the
// group instead (see `hasPlacementOnFile`'s doc comment) — so without this,
// toggling the switch OFF left every quorum slot in place both visually and
// on the backend, even though every ordinary signee's position was removed.
// Only the first `requiredSignatures` members of a group own a slot at all
// (see `isSigneeGroupQuorumSet`/`getQuorumGroupSlotPlaceholders`), so only
// those slots are ever iterated/cleared here.
async function removeAllQuorumSlotsOnSelectedFile() {
  const fileId = selectedFileId.value;
  if (!fileId || !createStore.document) return;

  const quorumGroups = (createStore.document.signeeGroups ?? []).filter(
    (group): group is SigneeGroup => isSigneeGroupQuorumSet(group)
  );

  await Promise.all(
    quorumGroups.flatMap((group) => {
      const requiredSignatures = group.requiredSignatures ?? 0;
      return Array.from({ length: requiredSignatures }, (_, slot) => slot)
        .filter(
          (slot) =>
            (findGroupSigningForSlot(group, fileId, slot)?.autographPositions
              ?.length ?? 0) > 0
        )
        .map((slot) =>
          persistQuorumSlotPlaceholder(
            createStore.document!.documentId,
            group,
            slot,
            fileId,
            null
          )
        );
    })
  );
}

async function removeAllSignatures() {
  createStore.isLoading = true;
  // Only signees that actually have a placement on the selected file and are
  // allowed to modify it need to be updated; each removal clears just the
  // selected file's positions while preserving other files.
  await Promise.all([
    ...signeesPlacedOnSelectedFile.value
      .filter((signee) => canModifyOnSelectedFile(signee))
      .map((signee) =>
        createStore.removeSignature({
          id: signee.signeeId,
          fileId: selectedFileId.value ?? undefined
        })
      ),
    removeAllQuorumSlotsOnSelectedFile()
  ]);
  createStore.isLoading = false;
}

async function updateSignaturesPreview(switchValue: boolean) {
  if (switchValue) {
    placeSignaturesAutomatically();
    return;
  }

  const confirmed = await confirmationDialog.value?.open(
    i18n.t('buttons.remove_signatures'),
    i18n.t('texts.remove_signatures_from_file_confirmation')
  );
  if (!confirmed) {
    // CustomSwitchWrapper already flipped OFF; remount to restore ON.
    switchRemountKey.value += 1;
    return;
  }

  await removeAllSignatures();
  await resetPlaceholders();
}
</script>
